import { useEffect, useRef, useState } from "react"
import { FaPause, FaPlay } from "react-icons/fa6"
import { GoMute, GoUnmute } from "react-icons/go"

type VideoPlayerProps = {
  url: string
  type: string
  thumbnail?: string
  muted?: boolean
  autoPlay?: boolean
}

export function VideoPlayer({
  url,
  thumbnail,
  muted = true,
  type = "buttons",
  autoPlay,
}: VideoPlayerProps) {
  const videoRef = useRef(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isHovered, setIsHovered] = useState(false)
  const [isMuted, setIsMuted] = useState(muted)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const hideTimeout = useRef(null)

  const togglePlay = () => {
    const video = videoRef.current
    if (!video) return

    if (video.paused) {
      video.play()
      setIsPlaying(true)
    } else {
      video.pause()
      setIsPlaying(false)
    }
  }
  const handleMute = () => {
    const video = videoRef.current
    if (!video) return
    video.muted = !video.muted
    setIsMuted(video.muted)
  }

  const handleMouseMove = () => {
    setIsHovered(true)
    clearTimeout(hideTimeout.current)
    hideTimeout.current = setTimeout(() => {
      setIsHovered(false)
    }, 3000)
  }

  useEffect(() => {
    return () => clearTimeout(hideTimeout.current)
  }, [])

  const embedUrl = url
    .replace("watch?v=", "embed/")
    .replace("shorts/", "embed/")
    .replace("youtu.be/", "youtube.com/embed/")

  if (type === "controls") {
    return (
      <div
        className="relative w-full rounded-xl overflow-hidden h-auto"
        onMouseEnter={() => setIsHovered(true)}
        onMouseMove={handleMouseMove}
        onMouseLeave={() => {
          clearTimeout(hideTimeout.current)
          setIsHovered(false)
        }}
      >
        <video
          ref={videoRef}
          src={embedUrl}
          autoPlay={autoPlay}
          muted={muted}
          loop
          playsInline
          poster={thumbnail}
          preload="metadata"
          className="w-full aspect-video object-cover"
          onClick={togglePlay}
          onPlay={() => setIsPlaying(true)}
          onTimeUpdate={() => {
            const video = videoRef.current
            if (video) setCurrentTime(video.currentTime)
          }}
          onLoadedMetadata={() => {
            const video = videoRef.current
            if (video) setDuration(video.duration)
          }}
        />
        <div
          className={`absolute bottom-4 left-0 w-full z-30 px-10 ${
            isPlaying && !isHovered
              ? "opacity-0 pointer-events-none"
              : "opacity-100"
          }`}
        >
          <input
            type="range"
            min={0}
            max={duration}
            value={currentTime}
            onChange={e => {
              const video = videoRef.current
              const time = Number(e.target.value)
              if (video) {
                video.currentTime = time
                setCurrentTime(time)
              }
            }}
            className="w-full"
          />
        </div>
        <button
          aria-label="Play video"
          onClick={togglePlay}
          className={`absolute inset-0 flex items-center justify-center transition-all duration-300 z-20
        ${
          isPlaying && !isHovered
            ? "opacity-0 pointer-events-none"
            : "opacity-100"
        }
      `}
        >
          <div className="relative !z-10 w-20 h-20 flex items-center justify-center bg-secondary-dark text-white rounded-full p-6 backdrop-blur-md transform transition-transform hover:scale-110 active:scale-95 shadow-xl">
            {isPlaying ? (
              <FaPause size={28} />
            ) : (
              <FaPlay size={30} className="ml-1" />
            )}
          </div>
          {!isPlaying && (
            <div className="animate-ripple bg-white/60 w-[100px] h-[100px] absolute -z-10 rounded-full" />
          )}
        </button>
        <button
          aria-label="Mute"
          onClick={handleMute}
          className={`absolute bottom-12 z-40 cursor-pointer right-10 bg-white/70 text-black w-10 h-10 inline-flex items-center justify-center rounded-full ${
            isPlaying && !isHovered
              ? "opacity-0 pointer-events-none"
              : "opacity-100"
          }`}
        >
          {isMuted ? <GoMute /> : <GoUnmute />}
        </button>
      </div>
    )
  } else {
    return (
      <iframe
        className="w-full rounded-3xl h-[200px] md:h-[500px]"
        src={embedUrl}
        title="YouTube video player"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    )
  }
}
